import { Booking, Customer, EquipmentType, Location, Redelivery, Transaction } from '.';

/*
 * Unit Status & Grade are now DB-backed lookups served by the backend.
 * The value stored on a unit / sent over the wire is the option's `label` string,
 * which is identical to the previous hardcoded enum values — so these are kept as
 * string aliases to preserve every existing annotation and `as X` cast with zero churn.
 * Dropdown option lists come from the LookupProvider (`useLookups`), not these types.
 */
export type UnitGrade = string;

export type UnitStatus = string;

/* Admin CRUD row shape for the unit-status / unit-grade lookup endpoints. */
export interface UnitLookupOption {
  id: number;
  code: string;
  label: string;
  active: boolean;
}

export class ActiveUnit {
  id: number = null as any;
  unitNumber?: string | null;

  equipmentType?: EquipmentType | null;
  sizeType?: string | null;
  statusReason?: string | null;
  unitGrade?: UnitGrade | null;
  unitStatus?: UnitStatus | null;

  active?: boolean | null;
  onHold?: boolean | null;
  holdNotes?: string | null;
  notes?: string | null;

  lastUpdatedBy?: string;
  createdDate?: string | null;
  createdBy?: string | null;
  deliveredDate?: number | string | Date | null;
  receivedDate?: number | string | Date | null;

  baseUnit?: ActiveUnit | null;
  booking?: Partial<Booking> | null;
  customer?: Customer | null;
  customerCode?: string;
  customerFullName?: string;
  customerLocation?: string;
  customerName?: string;
  gateInTransaction?: Transaction | null;
  gateOutTransaction?: Transaction | null;
  location?: Location | null;
  mountedUnits?: ActiveUnit[] | null;
  redelivery?: Redelivery;
  redeliveryId?: number;
  redeliveryNumber?: string;
  daysInYard?: number;
  manufacturedDate?: number | string | null;

  /* chassis */
  flatbed?: boolean | null;
  ownerChassis?: boolean | null;
  licensePlate?: string | null;
  fmcsaDate?: number | null;
  licensePlateExpirationDate?: number | null;

  /* container */
  hazard?: boolean | null;
  loaded?: boolean | null;
  seal0?: string | null;
  seal1?: string | null;
  seal2?: string | null;
  joinedSeals?: string | null;
  billOfLading?: string | null;
  weight?: number | null;

  /* reefer | geneset */
  carbCompliant?: boolean | null;
  fuelLevel?: number | null;
  humidity?: number | null;
  temperature?: number | null;
  ventilation?: string | null;

  constructor(unit?: ActiveUnit | null) {
    Object.assign(this, { ...unit });
  }
}

export class Chassis extends ActiveUnit {
  equipmentType?: EquipmentType.CHASSIS | null;
  constructor(chassis?: ActiveUnit) {
    super(chassis);
    this.equipmentType = EquipmentType.CHASSIS;
  }
}

export class Container extends ActiveUnit {
  equipmentType?: EquipmentType.CONTAINER | null;
  constructor(container?: ActiveUnit | null) {
    super(container);
    this.equipmentType = EquipmentType.CONTAINER;
  }
}

export class Genset extends ActiveUnit {
  equipmentType?: EquipmentType.GENSET | null;
  constructor(genset?: ActiveUnit | null) {
    super(genset);
    this.equipmentType = EquipmentType.GENSET;
  }
}

export interface UnitRow {
  id: number;
  createdDate: string;
  deliveredDate: number | string | Date | null;
  receivedDate: number | string | Date | null;
  manufacturedDate: number | string | Date | null;
  location: Location;
  stack: ActiveUnit[];
  equipmentType: EquipmentType;

  booking: Booking;
  redelivery: Redelivery;
  containerNumber: string;

  customerName: string | null;
  customerFullName: string | null;
  customerCode: string | null;
  customerLocation: string | null;
  containerCustomerName: string | null;
  containerCustomerFullName: string | null;
  containerCustomerCode: string | null;
  containerCustomerLocation: string | null;
  chassisCustomerName: string | null;
  chassisCustomerFullName: string | null;
  chassisCustomerCode: string | null;
  chassisCustomerLocation: string | null;
  gensetCustomerName: string | null;
  gensetCustomerFullName: string | null;
  gensetCustomerCode: string | null;
  gensetCustomerLocation: string | null;
  sizeType: string;
  unitStatus: UnitStatus;
  statusReason: string;
  daysInYard: number;

  chassisNumber: string;
  chassisCustomer: Customer;
  chassisSizeType: string;
  chassisStatus: UnitStatus;
  chassisStatusReason: string;

  gensetNumber: string;
  gensetCustomer: Customer;
  gensetSizeType: string;
  gensetStatus: UnitStatus;
  gensetStatusReason: string;

  unitGrade: UnitGrade;
  onHold: boolean;
  holdNotes: string;
  notes: string;
  createdBy: string;
  flatbed: boolean;
  ownerChassis: boolean;
  licensePlate: string;
  fmcsaDate: number;
  licensePlateExpirationDate: number;
  hazard: boolean;
  loaded: boolean;
  seal0: string;
  seal1: string;
  seal2: string;
  billOfLading: string;
  weight: number;
  humidity: number;
  temperature: number;
  ventilation: string;
  carbCompliant: boolean;
  fuelLevel: number;
}

export const isChassis = <T extends ActiveUnit>(unit: T): unit is T & Chassis =>
  unit.equipmentType === EquipmentType.CHASSIS;
export const isGenset = <T extends ActiveUnit>(unit: T): unit is T & Genset =>
  unit.equipmentType === EquipmentType.GENSET;
